import type { Limit } from "@/features/outputs/types";
import type { OutlineItem, Section } from "./types";

/**
 * The outline's pure parts: grounding badges, word totals and the moves the
 * outline panel offers. Sections arrive ordered (each parent before its
 * subsections) and nest one level.
 */

export type Grounding = "grounded" | "requires_grounding";

export const GROUNDING_LABELS: Record<Grounding, string> = {
  grounded: "Grounded",
  requires_grounding: "Requires grounding",
};

export const childrenOf = (sections: Section[], parentId: string) => sections.filter((s) => s.parent_id === parentId);

/**
 * A top-level section is grounded when it has sources of its own, or when it has
 * subsections and every one of them does. Subsections without sources draw on
 * their parent's.
 */
export function grounding(section: Section, sections: Section[]): Grounding {
  if (section.sources.length > 0) return "grounded";
  const children = childrenOf(sections, section.id);
  return children.length > 0 && children.every((c) => c.sources.length > 0) ? "grounded" : "requires_grounding";
}

/** Body words across the whole output. */
export const totalWords = (sections: Section[]) => sections.reduce((sum, s) => sum + s.word_count, 0);

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/** Length of `text` in the limit's unit. */
export function measure(text: string, limit: Limit): number {
  if (limit.unit === "words") return countWords(text);
  return limit.with_spaces ? text.length : text.replace(/\s/g, "").length;
}

export const toItems = (sections: Section[]): OutlineItem[] => sections.map((s) => ({ id: s.id, parent_id: s.parent_id }));

/** Each top-level item followed by its subsections. */
function blocks(items: OutlineItem[]): OutlineItem[][] {
  const result: OutlineItem[][] = [];
  for (const item of items) {
    if (item.parent_id === null) result.push([item]);
    else result.at(-1)?.push(item);
  }
  return result;
}

/**
 * Drag `activeId` to where `overId` is. A top-level section moves with its
 * subsections, among top-level sections; a subsection moves among its siblings.
 * Anything else leaves the order unchanged (null).
 */
export function moveItem(items: OutlineItem[], activeId: string, overId: string): OutlineItem[] | null {
  const active = items.find((i) => i.id === activeId);
  const over = items.find((i) => i.id === overId);
  if (!active || !over || activeId === overId) return null;

  if (active.parent_id === null) {
    const groups = blocks(items);
    const from = groups.findIndex((g) => g[0].id === activeId);
    const to = groups.findIndex((g) => g.some((i) => i.id === overId));
    if (from === to) return null;
    const [moved] = groups.splice(from, 1);
    groups.splice(to, 0, moved);
    return groups.flat();
  }
  if (over.parent_id !== active.parent_id) return null;
  const without = items.filter((i) => i.id !== activeId);
  const fromIndex = items.indexOf(active);
  const overIndex = without.findIndex((i) => i.id === overId);
  // Moving down lands after the target, moving up before it.
  without.splice(fromIndex < items.indexOf(over) ? overIndex + 1 : overIndex, 0, active);
  return without;
}

/** The top-level section above `id`, which it could be nested under (it has no subsections of its own). */
export function nestTarget(items: OutlineItem[], id: string): string | null {
  const groups = blocks(items);
  const index = groups.findIndex((g) => g[0].id === id);
  if (index <= 0 || groups[index].length > 1) return null;
  return groups[index - 1][0].id;
}

/** Make a top-level section the last subsection of the one above it. */
export function nest(items: OutlineItem[], id: string): OutlineItem[] | null {
  const parentId = nestTarget(items, id);
  if (!parentId) return null;
  return items.map((i) => (i.id === id ? { ...i, parent_id: parentId } : i));
}

/** Make a subsection top-level, just after its parent's block; later siblings stay with the parent. */
export function unnest(items: OutlineItem[], id: string): OutlineItem[] | null {
  const item = items.find((i) => i.id === id);
  if (!item?.parent_id) return null;
  const without = items.filter((i) => i.id !== id);
  const groups = blocks(without);
  const parentIndex = groups.findIndex((g) => g[0].id === item.parent_id);
  groups.splice(parentIndex + 1, 0, [{ id, parent_id: null }]);
  return groups.flat();
}
